// src/store/slices/annotationsSlice.ts
/**
 * Annotation layer management - shapes, text, drawings
 * Implements Section 5.6 of the specification
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AnnotationsState, AnnotationShape, AnnotationText, AnnotationStroke } from '../../types/redux';
import { nanoid } from 'nanoid';

const initialState: AnnotationsState = {
  shapes: [],
  texts: [],
  strokes: [],
  nextId: 1,
};

export const annotationsSlice = createSlice({
  name: 'annotations',
  initialState,
  reducers: {
    /**
     * Add a new shape annotation
     */
    addShape: (state, action: PayloadAction<Omit<AnnotationShape, 'id' | 'zIndex' | 'selected'>>) => {
      const shape: AnnotationShape = {
        ...action.payload,
        id: nanoid(),
        zIndex: state.nextId,
        selected: false,
      };
      state.shapes.push(shape);
      state.nextId += 1;
    },

    /**
     * Add a new text annotation
     */
    addText: (state, action: PayloadAction<Omit<AnnotationText, 'id' | 'zIndex' | 'selected'>>) => {
      const text: AnnotationText = {
        ...action.payload,
        id: nanoid(),
        zIndex: state.nextId,
        selected: false,
      };
      state.texts.push(text);
      state.nextId += 1;
    },

    /**
     * Add a new stroke annotation
     */
    addStroke: (state, action: PayloadAction<Omit<AnnotationStroke, 'id' | 'zIndex' | 'selected'>>) => {
      const stroke: AnnotationStroke = {
        ...action.payload,
        id: nanoid(),
        zIndex: state.nextId,
        selected: false,
      };
      state.strokes.push(stroke);
      state.nextId += 1;
    },

    /**
     * Update shape properties
     */
    updateShape: (state, action: PayloadAction<{ id: string; updates: Partial<AnnotationShape> }>) => {
      const { id, updates } = action.payload;
      const shape = state.shapes.find(s => s.id === id);
      if (shape) {
        Object.assign(shape, updates);
      }
    },

    /**
     * Update text properties
     */
    updateText: (state, action: PayloadAction<{ id: string; updates: Partial<AnnotationText> }>) => {
      const { id, updates } = action.payload;
      const text = state.texts.find(t => t.id === id);
      if (text) {
        Object.assign(text, updates);
      }
    },

    /**
     * Update stroke properties
     */
    updateStroke: (state, action: PayloadAction<{ id: string; updates: Partial<AnnotationStroke> }>) => {
      const { id, updates } = action.payload;
      const stroke = state.strokes.find(s => s.id === id);
      if (stroke) {
        Object.assign(stroke, updates);
      }
    },

    /**
     * Set selection state for annotations
     */
    setAnnotationSelected: (state, action: PayloadAction<{ id: string; selected: boolean }>) => {
      const { id, selected } = action.payload;
      
      // Check all annotation types
      const shape = state.shapes.find(s => s.id === id);
      const text = state.texts.find(t => t.id === id);
      const stroke = state.strokes.find(s => s.id === id);
      
      if (shape) shape.selected = selected;
      if (text) text.selected = selected;
      if (stroke) stroke.selected = selected;
    },

    /**
     * Delete annotation by ID
     */
    deleteAnnotation: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.shapes = state.shapes.filter(s => s.id !== id);
      state.texts = state.texts.filter(t => t.id !== id);
      state.strokes = state.strokes.filter(s => s.id !== id);
    },

    /**
     * Delete selected annotations
     */
    deleteSelectedAnnotations: (state) => {
      state.shapes = state.shapes.filter(s => !s.selected);
      state.texts = state.texts.filter(t => !t.selected);
      state.strokes = state.strokes.filter(s => !s.selected);
    },

    /**
     * Clear all annotations
     */
    clearAnnotations: (state) => {
      state.shapes = [];
      state.texts = [];
      state.strokes = [];
      state.nextId = 1;
    },

    /**
     * Clear pen/highlighter strokes only
     */
    clearStrokes: (state) => {
      state.strokes = [];
    },

    /**
     * Deselect all annotations
     */
    deselectAllAnnotations: (state) => {
      state.shapes.forEach(s => s.selected = false);
      state.texts.forEach(t => t.selected = false);
      state.strokes.forEach(s => s.selected = false);
    },

    /**
     * Bring annotation to front
     */
    bringToFront: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const maxZ = Math.max(
        ...state.shapes.map(s => s.zIndex),
        ...state.texts.map(t => t.zIndex),
        ...state.strokes.map(s => s.zIndex),
        0
      );
      
      const shape = state.shapes.find(s => s.id === id);
      const text = state.texts.find(t => t.id === id);
      const stroke = state.strokes.find(s => s.id === id);
      
      if (shape) shape.zIndex = maxZ + 1;
      if (text) text.zIndex = maxZ + 1;
      if (stroke) stroke.zIndex = maxZ + 1;
    },

    /**
     * Load annotations state from shared scene
     */
    loadAnnotationsState: (state, action: PayloadAction<AnnotationsState>) => {
      const { shapes, texts, strokes, nextId } = action.payload;
      state.shapes = shapes.map(s => ({ ...s, selected: false }));
      state.texts = texts.map(t => ({ ...t, selected: false }));
      state.strokes = strokes.map(s => ({ ...s, selected: false }));
      state.nextId = nextId;
    },

    /**
     * Convert annotation coordinates when switching between panning modes
     */
    convertAnnotationCoordinates: (state, action: PayloadAction<{ 
      fromPanningMode: boolean; 
      toPanningMode: boolean; 
      viewportOffset: { x: number; y: number };
      viewportScale: number;
      orientation?: 'horizontal' | 'vertical';
    }>) => {
      const { fromPanningMode, toPanningMode, viewportOffset, viewportScale, orientation = 'horizontal' } = action.payload;
      const isVertical = orientation === 'vertical';
      
      // Only convert if we're actually switching modes
      if (fromPanningMode === toPanningMode) return;
      
      // Only convert if there are annotations to convert
      const hasAnnotations = state.shapes.length > 0 || state.texts.length > 0 || state.strokes.length > 0;
      if (!hasAnnotations) return;
      
      // Convert shapes
      state.shapes.forEach(shape => {
        if (fromPanningMode && !toPanningMode) {
          // From panning to fixed: convert world coordinates to screen coordinates
          if (isVertical) {
            shape.geometry.y = shape.geometry.y * viewportScale + viewportOffset.y;
            if (shape.geometry.y2 !== undefined) {
              shape.geometry.y2 = shape.geometry.y2 * viewportScale + viewportOffset.y;
            }
          } else {
            shape.geometry.x = shape.geometry.x * viewportScale + viewportOffset.x;
            if (shape.geometry.x2 !== undefined) {
              shape.geometry.x2 = shape.geometry.x2 * viewportScale + viewportOffset.x;
            }
          }
          // Width, height, and radius remain unchanged (always in screen pixels)
        } else if (!fromPanningMode && toPanningMode) {
          // From fixed to panning: convert screen coordinates to world coordinates
          if (isVertical) {
            shape.geometry.y = (shape.geometry.y - viewportOffset.y) / viewportScale;
            if (shape.geometry.y2 !== undefined) {
              shape.geometry.y2 = (shape.geometry.y2 - viewportOffset.y) / viewportScale;
            }
          } else {
            shape.geometry.x = (shape.geometry.x - viewportOffset.x) / viewportScale;
            if (shape.geometry.x2 !== undefined) {
              shape.geometry.x2 = (shape.geometry.x2 - viewportOffset.x) / viewportScale;
            }
          }
          // Width, height, and radius remain unchanged (always in screen pixels)
        }
      });
      
      // Convert texts
      state.texts.forEach(text => {
        if (fromPanningMode && !toPanningMode) {
          if (isVertical) {
            text.position.y = text.position.y * viewportScale + viewportOffset.y;
          } else {
            text.position.x = text.position.x * viewportScale + viewportOffset.x;
          }
          // Other axis remains in screen pixels
        } else if (!fromPanningMode && toPanningMode) {
          if (isVertical) {
            text.position.y = (text.position.y - viewportOffset.y) / viewportScale;
          } else {
            text.position.x = (text.position.x - viewportOffset.x) / viewportScale;
          }
          // Other axis remains in screen pixels
        }
      });
      
      // Convert strokes
      state.strokes.forEach(stroke => {
        stroke.points.forEach(point => {
          if (fromPanningMode && !toPanningMode) {
            if (isVertical) {
              point.y = point.y * viewportScale + viewportOffset.y;
            } else {
              point.x = point.x * viewportScale + viewportOffset.x;
            }
            // Other axis remains in screen pixels
          } else if (!fromPanningMode && toPanningMode) {
            if (isVertical) {
              point.y = (point.y - viewportOffset.y) / viewportScale;
            } else {
              point.x = (point.x - viewportOffset.x) / viewportScale;
            }
            // Other axis remains in screen pixels
          }
        });
      });
    },
  },
});

export const {
  addShape,
  addText,
  addStroke,
  updateShape,
  updateText,
  updateStroke,
  setAnnotationSelected,
  deleteAnnotation,
  deleteSelectedAnnotations,
  clearAnnotations,
  clearStrokes,
  deselectAllAnnotations,
  bringToFront,
  loadAnnotationsState,
  convertAnnotationCoordinates,
} = annotationsSlice.actions;

export default annotationsSlice.reducer;